import type { FC } from 'react';
import { Text } from '@launchpad-ui/components';
import clsx from 'clsx';

import { HealthCheckButton } from '../HealthCheckButton/HealthCheckButton';
import { HealthCheckDocumentationLink } from '../HealthCheckDocumentationLink';
import type { HealthCheckOverallStatus } from '../types';

import styles from '../HealthCheck.module.css';

export type HealthCheckCardProps = {
  status: HealthCheckOverallStatus;
  children: React.ReactNode;
  /**
   * Optional URL to the documentation page in format 'home/experimentation/health-checks'.
   * If not provided, the documentation link will not be displayed.
   * */
  documentationUrl?: string;
  /** Optional callback when documentation link is clicked, e.g. for event tracking */
  onDocumentationLinkClick?: () => void;
  title?: string;
  showStatus?: boolean;
  subtitle?: string;
};

export const HealthCheckCard: FC<HealthCheckCardProps> = ({
  status,
  showStatus = false,
  title = 'Health check',
  subtitle = 'For the past 90 days:',
  documentationUrl,
  onDocumentationLinkClick,
  children,
}) => (
  <div className={styles.dialogContent}>
    <div className={styles.header}>
      <div className={styles.titleRow}>
        <div className={styles.titleSection}>
          {showStatus && status === 'loading' ? (
            <span
              className={clsx(styles.skeleton, styles.skeletonTitle)}
              role="progressbar"
              aria-label="Loading health check status"
            />
          ) : showStatus && status !== 'error' ? (
            <HealthCheckButton status={status} />
          ) : (
            title && (
              <Text slot="subtitle" size="large" bold>
                {title}
              </Text>
            )
          )}
        </div>
        {documentationUrl && <HealthCheckDocumentationLink url={documentationUrl} onClick={onDocumentationLinkClick} />}
      </div>
      {subtitle && <Text slot="subtitle">{subtitle}</Text>}
    </div>
    {children}
  </div>
);
